import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { ApiResponse } from "src/app/core/models/api-response.model";
import { InfoPanelStore } from "src/app/features/info-panel/info-panel-store";
import { TimelineService } from "src/app/features/timeline/services/timeline.service";
import { AssetDetailsService } from "src/app/features/asset-detail-panel/services/asset-details.service";
import { deepClone } from "src/app/helpers/utils/object.utils";
import { IndexedDBTraceService } from "src/app/shared/services/indexed-db/indexed-db-trace.service";
import { NetworkTraceService } from "src/app/shared/services/network-trace.service";
import { AssetType } from "src/app/shared/types/enums/map.enums";
import { IFeatureCollection, IStreamAssetsPayload } from "src/app/shared/types/interfaces/map.interface";
import { MapActions } from "../actions";
import { MapActionTypes } from "../actions/types";
import { Action } from "../ngrx.interface";
import { MapInteractionService } from "src/app/features/map/services/map-interaction.service";

@Injectable()
/**
 * geeral ngrx side effects
 */
export class MapEffects {
  private actions$ = inject(Actions);
  private networkTraceService = inject(NetworkTraceService);
  private assetDetailsService = inject(AssetDetailsService);
  private timelineService = inject(TimelineService);
  private indexedDBTraceService = inject(IndexedDBTraceService);
  private mapInteractionService = inject(MapInteractionService);

  Observable = Observable;
  infoPanelStore = inject(InfoPanelStore);

  getAssetDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActionTypes.GetAssetDetails),
      switchMap((action: Action): Observable<Action> => {
        if (action.payload.locationGroupAssets) {
          const payload: object = {
            asset_id: action.payload.assetIds[0],
            asset_type: action.payload.assetType,
            assets: action.payload.locationGroupAssets,
            ext_data: action.payload.extData,
            single_asset_pole: action.payload.singleAssetPole,
          };
          return of(MapActions.setAssetDetailsData({ payload }));
        } else {
          return this.assetDetailsService
            .requestAssetDetails(action.payload.assetIds, action.payload.assetType, action.payload.assetTypes)
            .pipe(
              map(res => {
                let payload: any = {};
                if (action.payload.assetType == AssetType.LOCATION_GROUP) {
                  // update the location group assets with the new payload.
                  payload = deepClone(action.payload.asset);
                  payload.assets = res.output.data;
                } else {
                  const data: object = res.output.data[0];
                  payload = data ? data : {};
                }
                return MapActions.setAssetDetailsData({ payload });
              }),
              catchError((error: any): Observable<Action> => {
                return of({
                  type: MapActionTypes.ServiceError,
                  payload: error,
                });
              })
            );
        }
      })
    )
  );

  getStreamAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActionTypes.GetStreamAssets),
      switchMap((action: Action): Observable<Action> => {
        const streamAssetsPayload = action.payload;
        return this.networkTraceService
          .getStreamAssets(
            streamAssetsPayload.assetId,
            streamAssetsPayload.substationId,
            streamAssetsPayload.direction,
            streamAssetsPayload.res
          )
          .pipe(
            switchMap((res: ApiResponse<{ geojson: IFeatureCollection }>) => {
              const featureCollection: IFeatureCollection = res.output.data.geojson;

              // Handle empty results - turn off loading and close panel
              if (featureCollection.features.length === 0) {
                this.networkTraceService.showError(streamAssetsPayload.direction, undefined);
                this.infoPanelStore.close();
                return of({ type: "NO_OP" });
              }

              // Check if info panel should be opened for stream table display
              const shouldSetStreamAssets = this.infoPanelStore.downstreamButtonClicked() || this.infoPanelStore.open();

              // Set stream overlay using MapInteractionService
              this.mapInteractionService.setStreamOverlay({
                featureCollection,
                origin: streamAssetsPayload.origin,
                shouldApplyMvtHighlight: true,
              });

              //The case of results cached in the database
              //remove cached results (res) so they aren't used on future iterations
              if (streamAssetsPayload.res) {
                const streamAssetsPayloadNew: IStreamAssetsPayload = {
                  assetId: streamAssetsPayload.assetId,
                  direction: streamAssetsPayload.direction,
                  origin: streamAssetsPayload.origin,
                  substationId: streamAssetsPayload.substationId,
                };

                if (shouldSetStreamAssets) {
                  return of(
                    MapActions.setStreamAssets({
                      payload: streamAssetsPayloadNew,
                    })
                  );
                } else {
                  // Only display trace overlay without opening stream table
                  return of({ type: "NO_OP" });
                }
              } else {
                //results were pulled from the api not the database.
                //store the results in the database
                const extension_name = this.networkTraceService.getCurrentExtension();

                this.indexedDBTraceService.saveTrace(
                  streamAssetsPayload.assetId,
                  streamAssetsPayload.direction,
                  streamAssetsPayload.substationId,
                  extension_name,
                  res,
                  this.networkTraceService.getRangeStringForKey(extension_name)
                );

                if (shouldSetStreamAssets) {
                  return of(
                    MapActions.setStreamAssets({
                      payload: streamAssetsPayload,
                    })
                  );
                } else {
                  // Only display trace overlay without opening stream table
                  return of({ type: "NO_OP" });
                }
              }
            }),
            catchError((error: any): Observable<Action> => {
              this.networkTraceService.showError(streamAssetsPayload.direction);
              // Close the info panel on error
              this.infoPanelStore.close();
              return of({
                type: MapActionTypes.ServiceError,
                payload: error,
              });
            })
          );
      })
    )
  );

  getGhcTimelineYears$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MapActionTypes.GetGhcTimelineYears),
      switchMap((action: Action): Observable<Action> => {
        return this.timelineService.requestGhcTimelineYears().pipe(
          map(res => {
            const payload: number[] = res.output.data;
            return MapActions.setGhcTimelineYears({ payload: payload });
          }),
          catchError((error: any): Observable<Action> => {
            return of({
              type: MapActionTypes.ServiceError,
              payload: error,
            });
          })
        );
      })
    )
  );
}
